import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import {
  IconCalendarTime,
  IconCalendarWeekFilled,
  IconChevronLeft,
  IconEdit,
  IconInfoCircle,
  IconPlus,
  IconShare3,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
  usePlotArea,
  useYAxisScale,
} from "recharts";

import MDialog from "@/components/ui/atoms/MDialog";
import { MButton } from "@/components/ui/atoms/MButtons";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import MDatePicker from "@/components/ui/molecules/MDatePicker";
import MLoader from "@/components/ui/atoms/MLoader";
import MOptionsMenu, {
  DropdownOption,
} from "@/components/ui/atoms/MOptionsMenu";
import DrawerPortal from "@/components/ui/atoms/DrawerPortal";
import { getMemberInfo } from "@/hooks/useMembers";
import { NAVIGATE_BACK_IN_HISTORY } from "@/hooks/useBackNavigation";
import { useVitalsReadings } from "@/hooks/useVitalsReadings";
import { usePrescriptionVitals } from "@/hooks/usePrescriptionVitals";
import { useVitalsTracking } from "@/hooks/useVitalsTracking";
import {
  VitalsChartPoint,
  VitalsParameter,
  VitalsRange,
  VitalsReading,
  VitalsValueRanges,
} from "@/types/vitals";

import vitalsTrackingService from "@/services/vitals-tracking.service";
import { triggerNativeVitalsShare } from "@/utils/nativeBridge";
import {
  getBpRange,
  getClassificationRange,
  getGlucoseRange,
} from "@/utils/vitalsRanges";
import {
  BP_AXIS,
  CHART_BOTTOM_MARGIN,
  CHART_HEIGHT,
  CHART_INSET,
  CHART_RIGHT_MARGIN,
  Y_AXIS_WIDTH,
  buildDateAxisLabels,
  computeChartWidth,
  computePlotWidth,
  computeSlotCount,
  computeYAxis,
  padSlots,
} from "@/utils/vitalsChart";
import AddHeightModal from "./AddHeightModal";
import AddReadingModal from "./AddReadingModal";
import EditRangeModal from "./EditRangeModal";
import VitalsRangeStrip from "./VitalsRangeStrip";
import PrescriptionVitalCard from "@/components/ui/molecules/PrescriptionVitalCard";
import styles from "./VitalsReadingsDetail.module.css";

// ── Constants ─────────────────────────────────────────────────────────────────

const GLUCOSE_CATEGORIES = [
  "Fasting",
  "Post-prandial",
  "Random",
  "Before bed",
] as const;

const CONTEXT_LABELS: Record<string, string> = {
  AFTER_WALK: "After exercise",
  BEFORE_SLEEP: "Before sleep",
  MISSED_MEDICATION: "Missed medicine",
  FEELING_UNWELL: "Feeling unwell",
  AFTER_MEDICATION: "After medicine",
  RESTING: "Resting",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function isSysOOR(v: number | undefined, r: [number, number] | undefined) {
  return !!r && v != null && (v < r[0] || v > r[1]);
}
function isDiaOOR(v: number | undefined, r: [number, number] | undefined) {
  return !!r && v != null && (v < r[0] || v > r[1]);
}
function isValOOR(v: number | undefined, r: [number, number] | undefined) {
  return !!r && v != null && (v < r[0] || v > r[1]);
}

/**
 * Bounds a reading is judged against — its own saved snapshot where present,
 * the member's current range otherwise. See getClassificationRange.
 */
function readingBpRange(reading: VitalsReading, range: VitalsRange) {
  return getBpRange(getClassificationRange(reading, range));
}
function glucoseRange(
  reading: VitalsReading,
  range: VitalsRange
): [number, number] | undefined {
  return getGlucoseRange(
    reading.values.category as string | undefined,
    getClassificationRange(reading, range)
  );
}

function fmtDate(d: Date | string) {
  return format(new Date(d), "d MMM yyyy, h:mm a");
}

function computeBMI(weightKg: number, heightCm: number): number {
  const m = heightCm / 100;
  return Math.round((weightKg / (m * m)) * 10) / 10;
}

function kgToLb(kg: number): number {
  return Math.round(kg * 2.2046 * 10) / 10;
}

function displayWeight(
  kg: number,
  unit: "kg" | "lb"
): { value: string; unitLabel: string } {
  if (unit === "lb")
    return {
      value: String(kgToLb(kg)),
      unitLabel: "lb",
    };
  return { value: String(Math.round(kg * 10) / 10), unitLabel: "kg" };
}

function displayHeightCm(cm: number): string {
  return `${cm} cm`;
}

function getContextLabel(r: VitalsReading): string | null {
  if (r.parameter === VitalsParameter.BP && r.context)
    return CONTEXT_LABELS[r.context] ?? r.context;
  if (r.parameter === VitalsParameter.GLUCOSE && r.values.category) {
    const cat = r.values.category as string;
    return cat;
  }
  return null;
}

// ── Chart data ────────────────────────────────────────────────────────────────

function toPoints(
  readings: VitalsReading[],
  range: VitalsRange,
  heightCm?: number
): VitalsChartPoint[] {
  return [...readings]
    .sort(
      (a, b) =>
        new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
    )
    .map((r) => {
      const recordedAt = new Date(r.recordedAt);
      const bmi =
        r.values.bmi != null
          ? r.values.bmi
          : heightCm && r.values.value != null
          ? computeBMI(r.values.value, heightCm)
          : undefined;
      return {
        slot: r.id,
        time: recordedAt.getTime(),
        fullDate: format(recordedAt, "d MMM, h:mm a"),
        systolic: r.values.systolic,
        diastolic: r.values.diastolic,
        value: r.values.value,
        bmi,
        pulse: r.values.pulse,
        id: r.id,
        sysOOR: isSysOOR(r.values.systolic, readingBpRange(r, range).systolic),
        diaOOR: isDiaOOR(
          r.values.diastolic,
          readingBpRange(r, range).diastolic
        ),
        valOOR: isValOOR(r.values.value, glucoseRange(r, range)),
      };
    });
}

/** Empty slot used to right-align a series shorter than the slot count. */
function makePadPoint(index: number): VitalsChartPoint {
  return {
    slot: `pad-${index}`,
    sysOOR: false,
    diaOOR: false,
    valOOR: false,
  };
}

/**
 * X-axis tick that prints one label per distinct date, under the leftmost
 * reading of that date. Padding slots and repeat dates render nothing.
 */
const makeAxisTick = (labels: Array<string | null>) =>
  function AxisTick(props: any) {
    const { x, y, payload, index } = props;
    const label = labels[payload?.index ?? index] ?? null;
    if (!label) return <g />;
    return (
      <text
        x={x}
        y={y}
        dy={12}
        textAnchor="middle"
        fontSize={10}
        fontFamily="Inter"
        fill="#606060"
      >
        {label}
      </text>
    );
  };

/**
 * Measures the chart viewport and derives the slot geometry from it: how many
 * readings fit, how wide the SVG must be, and whether that overflows (which is
 * the only thing that makes the chart scroll — and the swipe hint appear).
 */
function useChartLayout(
  pointCount: number,
  onScrollableChange?: (scrolls: boolean) => void
) {
  const measureRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const update = () => setViewportWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const slotCount = useMemo(
    () => computeSlotCount(computePlotWidth(viewportWidth)),
    [viewportWidth]
  );
  const chartWidth =
    viewportWidth > 0
      ? computeChartWidth(viewportWidth, pointCount, slotCount)
      : 0;
  const scrolls = viewportWidth > 0 && pointCount > slotCount;

  useEffect(() => {
    onScrollableChange?.(scrolls);
  }, [scrolls, onScrollableChange]);

  return { measureRef, slotCount, chartWidth };
}

// ── Custom chart dots ─────────────────────────────────────────────────────────

const DOT_OOR = "#D64561";
const DOT_SYS = "#8764BB";
const DOT_DIA = "#468BE5";
const DOT_VAL = "#8764BB";
const LINE_SYS = "#7046AE";
const LINE_DIA = "#468BE5";

const makeSysDot = (onSelect: (pt: VitalsChartPoint) => void) =>
  function SysDot(props: any) {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return <g />;
    const handleDown = (e: React.PointerEvent) => {
      e.stopPropagation();
      onSelect(payload);
    };
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={14}
          fill="transparent"
          onPointerDown={handleDown}
        />
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill={payload.sysOOR ? DOT_OOR : DOT_SYS}
          stroke="#fff"
          strokeWidth={2}
          onPointerDown={handleDown}
        />
      </g>
    );
  };

const makeDiaDot = (onSelect: (pt: VitalsChartPoint) => void) =>
  function DiaDot(props: any) {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return <g />;
    const handleDown = (e: React.PointerEvent) => {
      e.stopPropagation();
      onSelect(payload);
    };
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={14}
          fill="transparent"
          onPointerDown={handleDown}
        />
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill={payload.diaOOR ? DOT_OOR : DOT_DIA}
          stroke="#fff"
          strokeWidth={2}
          onPointerDown={handleDown}
        />
      </g>
    );
  };

const makeValDot = (color: string, onSelect: (pt: VitalsChartPoint) => void) =>
  function ValDot(props: any) {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return <g />;
    const handleDown = (e: React.PointerEvent) => {
      e.stopPropagation();
      onSelect(payload);
    };
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={14}
          fill="transparent"
          onPointerDown={handleDown}
        />
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill={payload.valOOR ? DOT_OOR : color}
          stroke="#fff"
          strokeWidth={2}
          onPointerDown={handleDown}
        />
      </g>
    );
  };

// ── Tooltip pill ──────────────────────────────────────────────────────────────

const TooltipPill: React.FC<{
  date: string;
  value: string;
  outOfRange?: boolean;
}> = ({ date, value, outOfRange }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.chartTooltip}>
      <span className={styles.ttDate}>{date}</span>
      <span className={styles.ttBullet}>•</span>
      <span className={styles.ttVal}>{value}</span>
      {outOfRange && (
        <>
          <span className={styles.ttBullet}>•</span>
          <span className={styles.ttOutOfRange}>
            {t("vitalsTracker.readingsDetail.outsideRange")}
          </span>
        </>
      )}
    </div>
  );
};

/**
 * Single-reading state: the value(s) against their reference range, shown until
 * a second reading exists to draw a trend between.
 */
const SingleReadingPanel: React.FC<{
  unit: string;
  date?: string;
  children: React.ReactNode;
}> = ({ unit, date, children }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.singleReading}>
      <div className={styles.chartTopRow}>
        <span className={styles.chartUnit}>{unit}</span>
        {date && <span className={styles.singleDate}>{date}</span>}
      </div>
      <div className={styles.singleStrips}>{children}</div>
      <p className={styles.singleHint}>
        {t("vitalsTracker.readingsDetail.addAnotherHint")}
      </p>
    </div>
  );
};

// ── Chart scroll wrapper ──────────────────────────────────────────────────────

const PAGE_READINGS = 6; // readings scrolled per arrow tap

const ChartScrollWrapper: React.FC<{
  children: React.ReactNode;
  dataLength: number;
  /** SVG width of the chart — 0 until the viewport has been measured. */
  contentWidth: number;
  /** Distance in px covered by one arrow tap. */
  pageWidth: number;
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
}> = ({
  children,
  dataLength,
  contentWidth,
  pageWidth,
  hasMore,
  onLoadMore,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasLeft, setHasLeft] = useState(false);
  const [isLocallyLoading, setIsLocallyLoading] = useState(false);
  const isAutoScrolling = useRef(false);
  const hasInitialized = useRef(false);
  const prevScrollWidth = useRef(0);
  const prevDataLength = useRef(dataLength);

  // Re-runs on data changes and on the first real measurement (contentWidth
  // goes 0 → measured), which is when the chart first becomes scrollable.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || contentWidth <= 0) return;
    const added = el.scrollWidth - prevScrollWidth.current;
    const overflows = el.scrollWidth > el.clientWidth + 1;
    const wasOverflowing = prevScrollWidth.current > el.clientWidth + 1;
    const dataChanged = dataLength !== prevDataLength.current;

    if (!hasInitialized.current) {
      // Open on the newest reading
      hasInitialized.current = true;
      if (overflows) {
        setHasLeft(true);
        isAutoScrolling.current = true;
        el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
        setTimeout(() => {
          isAutoScrolling.current = false;
        }, 800);
      }
    } else if (dataChanged && added > 0) {
      if (wasOverflowing && el.scrollLeft < 60) {
        // Already at the left edge — new data renders to our left, just stay here and show it
        setHasLeft(false);
      } else {
        // Instant correction keeps existing data visually stable (no shake)
        el.style.scrollBehavior = "auto";
        el.scrollLeft += added;
        el.style.scrollBehavior = "";
        // Then glide left to reveal the newly loaded data
        const target = Math.max(0, el.scrollLeft - pageWidth);
        isAutoScrolling.current = true;
        requestAnimationFrame(() => {
          el.scrollTo({ left: target, behavior: "smooth" });
          setTimeout(() => {
            isAutoScrolling.current = false;
            const cur = scrollRef.current;
            if (cur) setHasLeft(cur.scrollLeft > 4);
          }, 700);
        });
      }
    } else if (dataChanged && added < 0) {
      // Data was reset (refresh after add/edit/delete) — snap to newest
      el.scrollLeft = el.scrollWidth;
      setHasLeft(overflows);
    } else if (!dataChanged && added !== 0) {
      // Viewport resized (rotation, split view) — keep the newest in view
      el.scrollLeft = el.scrollWidth;
      setHasLeft(overflows);
    }
    prevScrollWidth.current = el.scrollWidth;
    prevDataLength.current = dataLength;
  }, [dataLength, contentWidth, pageWidth]);

  const triggerLoadMore = useCallback(async () => {
    if (isLocallyLoading || !hasMore || !onLoadMore) return;
    setIsLocallyLoading(true);
    try {
      await onLoadMore();
    } finally {
      setIsLocallyLoading(false);
    }
  }, [isLocallyLoading, hasMore, onLoadMore]);

  const handleScroll = useCallback(() => {
    if (isAutoScrolling.current) return;
    const el = scrollRef.current;
    if (!el) return;
    setHasLeft(el.scrollLeft > 4);
    if (el.scrollLeft < 60) {
      triggerLoadMore();
    }
  }, [triggerLoadMore]);

  const handleArrowClick = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollLeft < 60) {
      triggerLoadMore();
      return;
    }
    // Guard scroll so handleScroll doesn't fire loadMore mid-animation
    isAutoScrolling.current = true;
    el.scrollTo({
      left: Math.max(0, el.scrollLeft - pageWidth),
      behavior: "smooth",
    });
    setTimeout(() => {
      isAutoScrolling.current = false;
      const cur = scrollRef.current;
      if (!cur) return;
      setHasLeft(cur.scrollLeft > 4);
      // Trigger load only after scroll animation has fully settled
      if (cur.scrollLeft < 60) triggerLoadMore();
    }, 700);
  }, [triggerLoadMore, pageWidth]);

  const showArrow = hasLeft || (!!hasMore && !isLocallyLoading);

  return (
    <div className={styles.chartScrollWrapper}>
      <div
        ref={scrollRef}
        className={styles.chartScroll}
        onScroll={handleScroll}
      >
        {children}
      </div>
      {isLocallyLoading && (
        <div className={styles.chartLoadingMore}>
          <MLoader />
        </div>
      )}
      {showArrow && (
        <button
          className={styles.chartLeftIndicator}
          onClick={handleArrowClick}
          type="button"
          aria-label="Scroll to older readings"
        >
          <IconChevronLeft size={16} color="#7046ae" />
        </button>
      )}
    </div>
  );
};

// ── Chart panels ──────────────────────────────────────────────────────────────

interface ChartPanelPassThroughProps {
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
  /** Reports whether this chart overflows its viewport (drives the swipe hint). */
  onScrollableChange?: (scrolls: boolean) => void;
}

/**
 * Bounds over the member's whole history for a series, from the API. Folded
 * into the y-domain so it covers readings that have not been paged in yet —
 * without it, scrolling back to an older extreme would rescale the chart.
 */
type SeriesBounds = [number, number] | undefined;

/** Widest span covering both inputs; either may be absent. */
function mergeBounds(a: SeriesBounds, b: SeriesBounds): SeriesBounds {
  if (!a) return b;
  if (!b) return a;
  return [Math.min(a[0], b[0]), Math.max(a[1], b[1])];
}

/**
 * BMI bounds for readings that stored none — computeBMI rises monotonically
 * with weight at a fixed height, so the lightest and heaviest map straight onto
 * the lowest and highest BMI.
 */
function deriveBmiBounds(
  weightBounds: SeriesBounds,
  heightCm?: number
): SeriesBounds {
  if (!weightBounds || !heightCm) return undefined;
  return [
    computeBMI(weightBounds[0], heightCm),
    computeBMI(weightBounds[1], heightCm),
  ];
}

/**
 * Selection handlers for a chart panel. Padding slots carry no reading, so a
 * hover or tap that lands on one leaves the current selection alone.
 */
function usePointSelection(initialId?: string) {
  const [activeId, setActiveId] = useState<string | undefined>(initialId);
  const selectDot = useCallback((point?: VitalsChartPoint) => {
    if (point?.id) setActiveId(point.id);
  }, []);
  const selectPoint = useCallback(
    (event: any) => selectDot(event?.activePayload?.[0]?.payload),
    [selectDot]
  );
  return { activeId, selectDot, selectPoint };
}

const CHART_MARGIN = {
  top: CHART_INSET,
  right: CHART_RIGHT_MARGIN,
  left: 0,
  bottom: 0,
};

const AXIS_TICK_STYLE = {
  fontSize: 10,
  fill: "#9ca3af",
  fontFamily: "Inter",
};

const TOOLTIP_CURSOR = {
  stroke: "#36116d",
  strokeWidth: 1,
  strokeDasharray: "4 4",
};

/** Reference bands sit behind the series and stay lighter than it. */
const BAND_OPACITY = 0.12;
/** Gap between a band's label and the right edge of the plot. */
const BAND_LABEL_OFFSET = 8;

interface ReferenceBand {
  key: string;
  range: [number, number];
  color: string;
  labelColor: string;
  label: string;
}

/**
 * Reference ranges drawn as faint fills across the *whole* plot — including the
 * empty region left of the readings, since the range applies everywhere. Each
 * band is named at the right edge of the plot instead of in the legend.
 *
 * Recharts' own <ReferenceArea> stops at the first and last reading (it honours
 * the axis padding), which would leave the empty slots unshaded.
 */
const ChartReferenceBands: React.FC<{ bands: ReferenceBand[] }> = ({
  bands,
}) => {
  const plotArea = usePlotArea();
  const yScale = useYAxisScale();
  if (!plotArea || !yScale) return null;

  return (
    <g className={styles.referenceBands}>
      {bands.map((band) => {
        const edges = [
          yScale(band.range[0]),
          yScale(band.range[1]),
        ] as number[];
        if (edges.some((edge) => !Number.isFinite(edge))) return null;
        const top = Math.min(...edges);
        const height = Math.abs(edges[1] - edges[0]);
        return (
          <g key={band.key}>
            <rect
              x={plotArea.x}
              y={top}
              width={plotArea.width}
              height={height}
              fill={band.color}
              fillOpacity={BAND_OPACITY}
            />
            <text
              x={plotArea.x + plotArea.width - BAND_LABEL_OFFSET}
              y={top + height / 2}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={10}
              fontWeight={600}
              fill={band.labelColor}
            >
              {band.label}
            </text>
          </g>
        );
      })}
    </g>
  );
};

const BPChartPanel: React.FC<
  {
    data: VitalsChartPoint[];
    range: VitalsRange;
    systolicBounds?: SeriesBounds;
    diastolicBounds?: SeriesBounds;
  } & ChartPanelPassThroughProps
> = ({
  data,
  range,
  systolicBounds,
  diastolicBounds,
  hasMore,
  onLoadMore,
  onScrollableChange,
}) => {
  const { t } = useTranslation();
  const latestPt = data[data.length - 1] ?? null;
  // Tracked by id so the selection survives a data refresh, and falls back to
  // the newest reading when the selected one is deleted.
  const { activeId, selectDot, selectPoint } = usePointSelection(latestPt?.id);
  const activePt = data.find((p) => p.id === activeId) ?? latestPt;
  const SysDot = useMemo(() => makeSysDot(selectDot), [selectDot]);
  const DiaDot = useMemo(() => makeDiaDot(selectDot), [selectDot]);
  const { measureRef, slotCount, chartWidth } = useChartLayout(
    data.length,
    onScrollableChange
  );

  const bpRange = useMemo(() => getBpRange(range), [range]);
  // Domain covers every reading plus both active reference bounds, recomputed
  // whenever a reading is added/removed or the member edits their range.
  const yAxis = useMemo(
    () =>
      computeYAxis(
        [
          ...data.flatMap((p) => [p.systolic, p.diastolic]),
          ...bpRange.systolic,
          ...bpRange.diastolic,
          ...(systolicBounds ?? []),
          ...(diastolicBounds ?? []),
        ],
        BP_AXIS
      ),
    [data, bpRange, systolicBounds, diastolicBounds]
  );

  const bands = useMemo<ReferenceBand[]>(
    () => [
      {
        key: "systolic",
        range: bpRange.systolic,
        color: DOT_SYS,
        labelColor: LINE_SYS,
        label: t("vitalsTracker.readingsDetail.legend.sysRange"),
      },
      {
        key: "diastolic",
        range: bpRange.diastolic,
        color: DOT_DIA,
        labelColor: LINE_DIA,
        label: t("vitalsTracker.readingsDetail.legend.diaRange"),
      },
    ],
    [bpRange, t]
  );

  const points = useMemo(
    () => padSlots(data, slotCount, makePadPoint),
    [data, slotCount]
  );
  const AxisTick = useMemo(
    () => makeAxisTick(buildDateAxisLabels(points.map((p) => p.time ?? null))),
    [points]
  );

  if (data.length === 1 && latestPt) {
    return (
      <SingleReadingPanel unit="mmHg" date={latestPt.fullDate}>
        {latestPt.systolic != null && (
          <VitalsRangeStrip
            label={t("vitalsTracker.readingsDetail.legend.systolic")}
            value={latestPt.systolic}
            unit="mmHg"
            color={DOT_SYS}
            domain={yAxis.domain}
            range={bpRange.systolic}
            rangeLabel={t("vitalsTracker.readingsDetail.legend.refRange")}
            outOfRange={latestPt.sysOOR}
          />
        )}
        {latestPt.diastolic != null && (
          <VitalsRangeStrip
            label={t("vitalsTracker.readingsDetail.legend.diastolic")}
            value={latestPt.diastolic}
            unit="mmHg"
            color={DOT_DIA}
            domain={yAxis.domain}
            range={bpRange.diastolic}
            rangeLabel={t("vitalsTracker.readingsDetail.legend.refRange")}
            outOfRange={latestPt.diaOOR}
          />
        )}
      </SingleReadingPanel>
    );
  }

  return (
    <>
      <div className={styles.chartTopRow}>
        <span className={styles.chartUnit}>mmHg</span>
        {activePt && (
          <TooltipPill
            date={activePt.fullDate ?? ""}
            value={`${activePt.systolic}/${activePt.diastolic} mmHg${
              activePt.pulse ? ` · ${activePt.pulse} bpm` : ""
            }`}
            outOfRange={activePt.sysOOR || activePt.diaOOR}
          />
        )}
      </div>
      <div ref={measureRef} className={styles.chartMeasure}>
        <ChartScrollWrapper
          dataLength={data.length}
          contentWidth={chartWidth}
          pageWidth={(chartWidth / Math.max(1, points.length)) * PAGE_READINGS}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
        >
          {chartWidth > 0 && (
            <LineChart
              width={chartWidth}
              height={CHART_HEIGHT}
              data={points}
              margin={CHART_MARGIN}
              onMouseMove={selectPoint}
              onClick={selectPoint}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f0f0f0"
              />
              <XAxis
                dataKey="slot"
                type="category"
                interval={0}
                padding={{ left: CHART_INSET, right: CHART_INSET }}
                height={CHART_BOTTOM_MARGIN}
                tick={AxisTick}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={yAxis.domain}
                ticks={yAxis.ticks}
                interval={0}
                tick={AXIS_TICK_STYLE}
                axisLine={false}
                tickLine={false}
                width={Y_AXIS_WIDTH}
              />
              <ChartReferenceBands bands={bands} />
              <Line
                type="linear"
                dataKey="systolic"
                stroke={LINE_SYS}
                strokeWidth={4}
                dot={SysDot}
                activeDot={{ r: 5 }}
                connectNulls={false}
              />
              <Line
                type="linear"
                dataKey="diastolic"
                stroke={LINE_DIA}
                strokeWidth={4}
                dot={DiaDot}
                activeDot={{ r: 5 }}
                connectNulls={false}
              />
              <Tooltip content={() => null} cursor={TOOLTIP_CURSOR} />
            </LineChart>
          )}
        </ChartScrollWrapper>
      </div>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: DOT_SYS }} />
          {t("vitalsTracker.readingsDetail.legend.systolic")}
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: DOT_DIA }} />
          {t("vitalsTracker.readingsDetail.legend.diastolic")}
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: DOT_OOR }} />
          {t("vitalsTracker.readingsDetail.legend.outOfRange")}
        </span>
      </div>
    </>
  );
};

const SingleChartPanel: React.FC<
  {
    data: VitalsChartPoint[];
    range?: [number, number];
    unit: string;
    label: string;
    dataKey?: "value" | "bmi";
    lineColor?: string;
    bounds?: SeriesBounds;
  } & ChartPanelPassThroughProps
> = ({
  data,
  range,
  unit,
  label,
  dataKey = "value",
  lineColor = LINE_SYS,
  bounds,
  hasMore,
  onLoadMore,
  onScrollableChange,
}) => {
  const { t } = useTranslation();
  const latestPt = data[data.length - 1] ?? null;
  // Tracked by id so the selection survives a data refresh, and falls back to
  // the newest reading when the selected one is deleted.
  const { activeId, selectDot, selectPoint } = usePointSelection(latestPt?.id);
  const activePt = data.find((p) => p.id === activeId) ?? latestPt;
  const dotColor = lineColor === LINE_SYS ? DOT_VAL : lineColor;
  const valDot = useMemo(
    () => makeValDot(dotColor, selectDot),
    [dotColor, selectDot]
  );
  const { measureRef, slotCount, chartWidth } = useChartLayout(
    data.length,
    onScrollableChange
  );

  // Domain covers every reading plus the active reference bounds, recomputed
  // whenever a reading is added/removed or the member edits their range.
  const yAxis = useMemo(
    () =>
      computeYAxis([
        ...data.map((p) => p[dataKey]),
        ...(range ?? []),
        ...(bounds ?? []),
      ]),
    [data, dataKey, range, bounds]
  );

  const bands = useMemo<ReferenceBand[]>(
    () =>
      range
        ? [
            {
              key: "range",
              range,
              color: dotColor,
              labelColor: lineColor,
              label: t("vitalsTracker.readingsDetail.legend.refRange"),
            },
          ]
        : [],
    [range, dotColor, lineColor, t]
  );

  const points = useMemo(
    () => padSlots(data, slotCount, makePadPoint),
    [data, slotCount]
  );
  const AxisTick = useMemo(
    () => makeAxisTick(buildDateAxisLabels(points.map((p) => p.time ?? null))),
    [points]
  );

  if (data.length === 1 && latestPt && latestPt[dataKey] != null) {
    return (
      <SingleReadingPanel unit={unit} date={latestPt.fullDate}>
        <VitalsRangeStrip
          label={label}
          value={latestPt[dataKey] as number}
          unit={unit}
          color={dotColor}
          domain={yAxis.domain}
          range={range}
          rangeLabel={t("vitalsTracker.readingsDetail.legend.refRange")}
          outOfRange={dataKey === "value" && latestPt.valOOR}
        />
      </SingleReadingPanel>
    );
  }

  return (
    <>
      <div className={styles.chartTopRow}>
        <span className={styles.chartUnit}>{unit}</span>
        {activePt && (
          <TooltipPill
            date={activePt.fullDate ?? ""}
            value={`${activePt[dataKey]} ${unit}`}
            outOfRange={dataKey === "value" && activePt.valOOR}
          />
        )}
      </div>
      <div ref={measureRef} className={styles.chartMeasure}>
        <ChartScrollWrapper
          dataLength={data.length}
          contentWidth={chartWidth}
          pageWidth={(chartWidth / Math.max(1, points.length)) * PAGE_READINGS}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
        >
          {chartWidth > 0 && (
            <LineChart
              width={chartWidth}
              height={CHART_HEIGHT}
              data={points}
              margin={CHART_MARGIN}
              onMouseMove={selectPoint}
              onClick={selectPoint}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f0f0f0"
              />
              <XAxis
                dataKey="slot"
                type="category"
                interval={0}
                padding={{ left: CHART_INSET, right: CHART_INSET }}
                height={CHART_BOTTOM_MARGIN}
                tick={AxisTick}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={yAxis.domain}
                ticks={yAxis.ticks}
                interval={0}
                tick={AXIS_TICK_STYLE}
                axisLine={false}
                tickLine={false}
                width={Y_AXIS_WIDTH}
              />
              {bands.length > 0 && <ChartReferenceBands bands={bands} />}
              <Line
                type="linear"
                dataKey={dataKey}
                stroke={lineColor}
                strokeWidth={4}
                dot={valDot}
                activeDot={{ r: 5 }}
                connectNulls={false}
              />
              <Tooltip content={() => null} cursor={TOOLTIP_CURSOR} />
            </LineChart>
          )}
        </ChartScrollWrapper>
      </div>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: dotColor }} />
          {label}
        </span>
        {dataKey === "value" && range && (
          <span className={styles.legendItem}>
            <span
              className={styles.legendDot}
              style={{ background: DOT_OOR }}
            />
            {t("vitalsTracker.readingsDetail.legend.outOfRange")}
          </span>
        )}
      </div>
    </>
  );
};

// ── Chart view ────────────────────────────────────────────────────────────────

export const ChartView: React.FC<
  {
    readings: VitalsReading[];
    range: VitalsRange;
    param: VitalsParameter;
    total: number;
    /** Whole-history bounds per series, keyed as VitalsValueRanges describes. */
    valueRanges?: VitalsValueRanges;
    glucoseFilter?: string | null;
    onBmiInfoClick?: () => void;
  } & ChartPanelPassThroughProps
> = ({
  readings,
  range,
  param,
  total,
  valueRanges,
  glucoseFilter = null,
  onBmiInfoClick,
  hasMore,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const [chartWeightUnit, setChartWeightUnit] = useState<"kg" | "lb">("kg");
  // Panels report their own overflow — the swipe hint only shows when a chart
  // genuinely scrolls, never for a series that fits in the visible slots.
  const [scrollable, setScrollable] = useState<Record<string, boolean>>({});
  const handlerCache = useRef<Record<string, (scrolls: boolean) => void>>({});
  const scrollableHandler = useCallback((key: string) => {
    if (!handlerCache.current[key]) {
      handlerCache.current[key] = (scrolls: boolean) =>
        setScrollable((prev) =>
          prev[key] === scrolls ? prev : { ...prev, [key]: scrolls }
        );
    }
    return handlerCache.current[key];
  }, []);
  const heightCm = range.height;

  if (readings.length === 0) {
    return (
      <p className={styles.emptyText}>
        {t("vitalsTracker.readingsDetail.noReadings")}
      </p>
    );
  }

  // The BMI chart carries its own header and hint, so it is excluded here.
  const mainScrolls = Object.entries(scrollable).some(
    ([key, scrolls]) => key !== "bmi" && scrolls
  );

  return (
    <div className={styles.chartSection}>
      <div className={styles.chartHeaderRow}>
        <div className={styles.chartHeaderLeft}>
          {param === VitalsParameter.WEIGHT && (
            <span className={styles.chartSectionTitle}>
              {t("vitalsTracker.weight")}
            </span>
          )}
          <span className={styles.readingsCount}>
            {t("vitalsTracker.readingsDetail.readingCount", { count: total })}
          </span>
        </div>
        {mainScrolls && (
          <span className={styles.swipeHint}>
            {t("vitalsTracker.readingsDetail.swipeHint")}
          </span>
        )}
      </div>

      {param === VitalsParameter.BP && (
        <div className={styles.chartCard}>
          <BPChartPanel
            data={toPoints(readings, range)}
            range={range}
            systolicBounds={valueRanges?.systolic}
            diastolicBounds={valueRanges?.diastolic}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            onScrollableChange={scrollableHandler("bp")}
          />
        </div>
      )}

      {param === VitalsParameter.GLUCOSE && (
        <div className={styles.glucoseCharts}>
          {(glucoseFilter ? [glucoseFilter] : GLUCOSE_CATEGORIES).map((cat) => {
            const catReadings = readings.filter(
              (r) => r.values.category === cat
            );
            return (
              <div key={cat} className={styles.glucoseCatGroup}>
                <div className={styles.chartSectionHeader}>
                  <span className={styles.chartSectionTitle}>{cat}</span>
                  <span className={styles.chartSectionCount}>
                    {t("vitalsTracker.readingsDetail.readingCount", {
                      count: catReadings.length,
                    })}
                  </span>
                </div>
                <div className={styles.chartCard}>
                  {catReadings.length === 0 ? (
                    <p className={styles.emptyCategory}>
                      {t("vitalsTracker.readingsDetail.noContextReadings", {
                        context: cat.toLowerCase(),
                      })}
                    </p>
                  ) : (
                    <SingleChartPanel
                      data={toPoints(catReadings, range)}
                      range={getGlucoseRange(cat, range)}
                      unit="mg/dL"
                      label={cat}
                      bounds={valueRanges?.[cat]}
                      hasMore={hasMore}
                      onLoadMore={onLoadMore}
                      onScrollableChange={scrollableHandler(cat)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {param === VitalsParameter.HEIGHT && (
        <div className={styles.chartCard}>
          <SingleChartPanel
            data={toPoints(readings, range)}
            range={undefined}
            unit="cm"
            label={t("vitalsTracker.heightVital")}
            bounds={valueRanges?.value}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            onScrollableChange={scrollableHandler("height")}
          />
        </div>
      )}

      {param === VitalsParameter.WEIGHT &&
        (() => {
          const pts = toPoints(readings, range, heightCm);
          const weightPts =
            chartWeightUnit === "lb"
              ? pts.map((p) => ({
                  ...p,
                  value: p.value != null ? kgToLb(p.value) : undefined,
                }))
              : pts;
          // Server bounds are stored in kg — follow the unit toggle
          const kgBounds = valueRanges?.value;
          const weightBounds: SeriesBounds =
            kgBounds && chartWeightUnit === "lb"
              ? [kgToLb(kgBounds[0]), kgToLb(kgBounds[1])]
              : kgBounds;
          // The BMI series mixes values stored at recording time with ones
          // derived here from the configured height — bound both halves, or
          // paging in an older reading would rescale the BMI chart.
          const bmiBounds = mergeBounds(
            valueRanges?.bmi,
            deriveBmiBounds(valueRanges?.valueWithoutBmi, heightCm)
          );
          const bmiPts = pts.filter((p) => p.bmi != null);
          const hasBmi = heightCm != null && bmiPts.length > 0;
          return (
            <div className={styles.glucoseCharts}>
              <div className={styles.chartWeightToggleRow}>
                {(["kg", "lb"] as const).map((u) => (
                  <button
                    key={u}
                    type="button"
                    className={`${styles.chartWeightUnitBtn}${
                      chartWeightUnit === u
                        ? ` ${styles.chartWeightUnitActive}`
                        : ""
                    }`}
                    onClick={() => setChartWeightUnit(u)}
                  >
                    {u}
                  </button>
                ))}
              </div>
              <div className={styles.chartCard}>
                <SingleChartPanel
                  data={weightPts}
                  range={undefined}
                  unit={chartWeightUnit}
                  label={t("vitalsTracker.weight")}
                  bounds={weightBounds}
                  hasMore={hasMore}
                  onLoadMore={onLoadMore}
                  onScrollableChange={scrollableHandler("weight")}
                />
              </div>
              {hasBmi && (
                <>
                  <div className={styles.bmiSectionHeader}>
                    <span className={styles.bmiSectionTitle}>BMI</span>
                    <button
                      type="button"
                      className={styles.bmiInfoCircle}
                      onClick={onBmiInfoClick}
                      aria-label="BMI information"
                    >
                      <IconInfoCircle size={12} color="#7046ae" />
                    </button>
                    <span className={styles.readingsCount}>
                      {t("vitalsTracker.readingsDetail.readingCount", {
                        count: bmiPts.length,
                      })}
                    </span>
                    <span className={styles.bmiSectionSpacer} />
                    {scrollable.bmi && (
                      <span className={styles.swipeHint}>
                        {t("vitalsTracker.readingsDetail.swipeHint")}
                      </span>
                    )}
                  </div>
                  <div className={styles.chartCard}>
                    <SingleChartPanel
                      data={pts}
                      range={undefined}
                      unit="BMI"
                      label="BMI"
                      dataKey="bmi"
                      lineColor="#468BE5"
                      bounds={bmiBounds}
                      hasMore={hasMore}
                      onLoadMore={onLoadMore}
                      onScrollableChange={scrollableHandler("bmi")}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })()}
    </div>
  );
};

// ── Table row ─────────────────────────────────────────────────────────────────

const ReadingRow: React.FC<{
  reading: VitalsReading;
  range: VitalsRange;
  param: VitalsParameter;
  onEdit: (r: VitalsReading) => void;
  onDelete: (r: VitalsReading) => void;
  weightUnit?: "kg" | "lb";
}> = ({ reading, range, param, onEdit, onDelete, weightUnit = "kg" }) => {
  const { t } = useTranslation();
  const sOOR = isSysOOR(
    reading.values.systolic,
    readingBpRange(reading, range).systolic
  );
  const dOOR = isDiaOOR(
    reading.values.diastolic,
    readingBpRange(reading, range).diastolic
  );
  const vOOR = isValOOR(reading.values.value, glucoseRange(reading, range));
  const ctxLabel = getContextLabel(reading);

  const options: DropdownOption[] = [
    {
      id: "edit",
      label: t("vitalsTracker.readingsDetail.editReading"),
      icon: <IconEdit size={16} />,
      onClick: () => onEdit(reading),
    },
    {
      id: "delete",
      label: t("vitalsTracker.readingsDetail.deleteReading"),
      icon: <IconTrash size={16} />,
      onClick: () => onDelete(reading),
      delete: true,
    },
  ];

  return (
    <div className={styles.tableRow}>
      <div className={styles.rowContent}>
        {/* Date */}
        <span className={styles.rowDate}>{fmtDate(reading.recordedAt)}</span>

        {/* Value + badge */}
        <div className={styles.rowValueLine}>
          {param === VitalsParameter.BP ? (
            <div className={styles.rowValUnit}>
              <span className={styles.rowBpValue}>
                <span style={{ color: sOOR ? "#c33f58" : "#36116d" }}>
                  {reading.values.systolic ?? "--"}
                </span>
                <span style={{ color: "#797979" }}>/</span>
                <span style={{ color: dOOR ? "#c33f58" : "#36116d" }}>
                  {reading.values.diastolic ?? "--"}
                </span>
              </span>
              <span className={styles.rowUnit}>mmHg</span>
            </div>
          ) : param === VitalsParameter.HEIGHT ? (
            <div className={styles.rowValUnit}>
              <span className={styles.rowValue}>
                {reading.values.value ?? "--"}
              </span>
              <span className={styles.rowUnit}>cm</span>
            </div>
          ) : param === VitalsParameter.WEIGHT ? (
            <span className={styles.rowWeightBmi}>
              {(() => {
                const wDisp =
                  reading.values.value != null
                    ? displayWeight(reading.values.value, weightUnit)
                    : null;
                const heightCm = range.height;
                const bmiVal =
                  reading.values.bmi ??
                  (heightCm && reading.values.value != null
                    ? computeBMI(reading.values.value, heightCm)
                    : null);
                return (
                  <>
                    <span className={styles.rowWeightVal}>
                      {wDisp?.value ?? "--"}
                    </span>
                    <span className={styles.rowWeightUnit}>
                      {wDisp?.unitLabel ?? weightUnit}
                    </span>
                    {bmiVal != null && (
                      <>
                        <span className={styles.rowInlineDivider}> | </span>
                        <span className={styles.rowBmiVal}>{bmiVal}</span>
                        <span className={styles.rowBmiLabel}>BMI</span>
                      </>
                    )}
                  </>
                );
              })()}
            </span>
          ) : (
            <div className={styles.rowValUnit}>
              <span
                className={`${styles.rowValue}${
                  vOOR ? ` ${styles.rowValueOOR}` : ""
                }`}
              >
                {reading.values.value ?? "--"}
              </span>
              <span className={styles.rowUnit}>mg/dL</span>
            </div>
          )}
          {ctxLabel && <span className={styles.badge}>{ctxLabel}</span>}
        </div>

        {/* Pulse */}
        {param === VitalsParameter.BP && reading.values.pulse != null && (
          <p className={styles.rowMeta}>
            {reading.values.pulse}{" "}
            <span className={styles.rowMetaUnit}>BPM</span>
          </p>
        )}

        {/* Note */}
        {reading.note && <p className={styles.rowNote}>"{reading.note}"</p>}
      </div>
      <MOptionsMenu options={options} />
    </div>
  );
};

// ── Table view ────────────────────────────────────────────────────────────────

const TableView: React.FC<{
  readings: VitalsReading[];
  range: VitalsRange;
  param: VitalsParameter;
  onEdit: (r: VitalsReading) => void;
  onDelete: (r: VitalsReading) => void;
  weightUnit?: "kg" | "lb";
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  glucoseFilter?: string | null;
}> = ({
  readings,
  range,
  param,
  onEdit,
  onDelete,
  weightUnit = "kg",
  hasMore,
  isLoadingMore,
  onLoadMore,
  glucoseFilter,
}) => {
  const { t } = useTranslation();
  const sorted = useMemo(
    () =>
      [...readings].sort(
        (a, b) =>
          new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
      ),
    [readings]
  );

  if (param === VitalsParameter.GLUCOSE) {
    const categoriesToShow = glucoseFilter
      ? GLUCOSE_CATEGORIES.filter((cat) => cat === glucoseFilter)
      : GLUCOSE_CATEGORIES;
    return (
      <div className={styles.glucoseTableGroups}>
        {categoriesToShow.map((cat) => {
          const catRows = sorted.filter((r) => r.values.category === cat);
          return (
            <div key={cat} className={styles.glucoseCatGroup}>
              <div className={styles.chartSectionHeader}>
                <span className={styles.chartSectionTitle}>{cat}</span>
                <span className={styles.chartSectionCount}>
                  {catRows.length} readings
                </span>
              </div>
              {catRows.length === 0 ? (
                <div className={styles.chartCard}>
                  <p className={styles.emptyCategory}>
                    {t("vitalsTracker.readingsDetail.noContextReadings", {
                      context: cat.toLowerCase(),
                    })}
                  </p>
                </div>
              ) : (
                <div className={styles.tableList}>
                  {catRows.map((r) => (
                    <ReadingRow
                      key={r.id}
                      reading={r}
                      range={range}
                      param={param}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      weightUnit={weightUnit}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <p className={styles.emptyText}>
        {t("vitalsTracker.readingsDetail.noReadings")}
      </p>
    );
  }

  return (
    <MInfiniteScroll
      data={sorted}
      hasMore={hasMore}
      isLoading={isLoadingMore}
      onLoadMore={onLoadMore}
      className={styles.tableList}
      gap={0}
      renderItem={(r) => (
        <ReadingRow
          key={r.id}
          reading={r}
          range={range}
          param={param}
          onEdit={onEdit}
          onDelete={onDelete}
          weightUnit={weightUnit}
        />
      )}
    />
  );
};

// ── Latest reading card ───────────────────────────────────────────────────────

const LatestReadingCard: React.FC<{
  reading: VitalsReading;
  range: VitalsRange;
  memberName: string;
  onEditRange: () => void;
  weightUnit?: "kg" | "lb";
  onWeightUnitChange?: (u: "kg" | "lb") => void;
  onAddHeight?: () => void;
}> = ({
  reading,
  range,
  memberName,
  onEditRange,
  weightUnit = "kg",
  onWeightUnitChange,
  onAddHeight,
}) => {
  const { t } = useTranslation();
  const sOOR = isSysOOR(
    reading.values.systolic,
    readingBpRange(reading, range).systolic
  );
  const dOOR = isDiaOOR(
    reading.values.diastolic,
    readingBpRange(reading, range).diastolic
  );
  // Flags follow the range saved with the reading; the displayed reference
  // range below shows the member's CURRENT target, which is what they edit.
  const savedGlucoseRange = glucoseRange(reading, range);
  const activeGlucoseRange = getGlucoseRange(
    reading.values.category as string | undefined,
    range
  );
  const vOOR = isValOOR(reading.values.value, savedGlucoseRange);
  const ctxLabel = getContextLabel(reading);

  const oorLabel = (() => {
    if (
      reading.parameter === VitalsParameter.GLUCOSE &&
      vOOR &&
      savedGlucoseRange &&
      reading.values.value != null
    )
      return reading.values.value < savedGlucoseRange[0]
        ? "Below range"
        : "Above range";
    if (reading.parameter === VitalsParameter.BP && (sOOR || dOOR))
      return "Out of range";
    return null;
  })();

  const rangeNumbers =
    reading.parameter === VitalsParameter.BP &&
    range.systolic?.length >= 2 &&
    range.diastolic?.length >= 2
      ? `${range.systolic[0]}-${range.systolic[1]} / ${range.diastolic[0]}-${range.diastolic[1]}`
      : reading.parameter === VitalsParameter.GLUCOSE && activeGlucoseRange
      ? `${activeGlucoseRange[0]}-${activeGlucoseRange[1]}`
      : null;

  const refLabel =
    reading.parameter === VitalsParameter.GLUCOSE && ctxLabel
      ? `Reference range • ${ctxLabel}`
      : "Reference range";

  const heightCm = range.height;
  const wDisp =
    reading.values.value != null
      ? displayWeight(reading.values.value, weightUnit)
      : null;
  const bmiVal =
    heightCm && reading.values.value != null
      ? computeBMI(reading.values.value, heightCm)
      : reading.values.bmi;

  return (
    <div className={styles.latestCard}>
      {/* Title + date */}
      <div className={styles.latestTitleRow}>
        <span className={styles.latestName}>{memberName} latest reading</span>
        <span className={styles.latestDate}>
          {format(new Date(reading.recordedAt), "d MMM yyyy • h:mm a")}
        </span>
      </div>

      <div className={styles.latestValues}>
        {/* BP: big value + divider + BPM */}
        {reading.parameter === VitalsParameter.BP && (
          <div className={styles.bpRow}>
            <div className={styles.bpLeft}>
              <span className={styles.bpBig}>
                <span className={sOOR ? styles.bpOOR : styles.bpNorm}>
                  {reading.values.systolic ?? "--"}
                </span>
                <span className={styles.bpSlash}>/</span>
                <span className={dOOR ? styles.bpOOR : styles.bpNorm}>
                  {reading.values.diastolic ?? "--"}
                </span>
              </span>
              <span className={styles.bpUnit}>mmHg</span>
            </div>
            <div className={styles.bpDivider} />
            <div className={styles.bpmLeft}>
              <span className={styles.bpmVal}>
                {reading.values.pulse ?? "--"}
              </span>
              <span className={styles.bpmLabel}>BPM • Pulse</span>
            </div>
          </div>
        )}

        {/* Height: single value */}
        {reading.parameter === VitalsParameter.HEIGHT && (
          <p className={styles.singleVal}>
            {`${reading.values.value ?? "--"} cm`}
          </p>
        )}

        {/* Glucose: single value */}
        {reading.parameter === VitalsParameter.GLUCOSE && (
          <div className={styles.glucoseValRow}>
            <span
              className={`${styles.singleVal}${
                vOOR ? ` ${styles.singleValOOR}` : ""
              }`}
            >
              {reading.values.value ?? "--"}
            </span>
            <span className={styles.latestUnit}>mg/dL</span>
          </div>
        )}

        {/* Weight: kg/lb toggle + value + BMI */}
        {reading.parameter === VitalsParameter.WEIGHT && (
          <>
            {/* Unit toggle */}
            <div className={styles.weightUnitToggle}>
              {(["kg", "lb"] as const).map((u) => (
                <button
                  key={u}
                  type="button"
                  className={`${styles.weightUnitBtn}${
                    weightUnit === u ? ` ${styles.weightUnitActive}` : ""
                  }`}
                  onClick={() => onWeightUnitChange?.(u)}
                >
                  {u}
                </button>
              ))}
            </div>

            {/* No height: big value + divider + add height prompt */}
            {!heightCm ? (
              <>
                <p className={styles.weightBig}>
                  {wDisp?.value ?? "--"}
                  <span className={styles.weightUnit}>
                    {" "}
                    {wDisp?.unitLabel ?? weightUnit}
                  </span>
                </p>
                <div className={styles.weightNoHeightDivider} />
                <div className={styles.noHeightBlock}>
                  <p className={styles.noHeightText}>
                    {t("vitalsTracker.height.addHeightPrompt")}
                  </p>
                  <button
                    type="button"
                    className={styles.addHeightBtn}
                    onClick={onAddHeight}
                  >
                    {t("vitalsTracker.height.addHeightBtn")}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Weight + BMI side by side */}
                <div className={styles.weightBmiRow}>
                  <div className={styles.weightValBlock}>
                    <span className={styles.weightBig}>
                      {wDisp?.value ?? "--"}
                    </span>
                    <span className={styles.weightUnit}>
                      {wDisp?.unitLabel ?? weightUnit}
                    </span>
                  </div>
                  <div className={styles.weightBmiDivider} />
                  <div className={styles.bmiBlock}>
                    <span className={styles.bmiBig}>{bmiVal ?? "--"}</span>
                    <span className={styles.bmiLabel}>
                      {t("vitalsTracker.height.bmiLabel")}
                    </span>
                  </div>
                </div>

                {/* Divider between weight/BMI and height */}
                <div className={styles.weightHeightDivider} />

                {/* Height row with edit */}
                <div className={styles.heightRow}>
                  <span className={styles.heightLabel}>
                    {t("vitalsTracker.height.heightLabel")}
                  </span>
                  <span className={styles.heightValue}>
                    {displayHeightCm(heightCm)}
                  </span>
                  <button
                    type="button"
                    className={styles.heightEditBtn}
                    onClick={onAddHeight}
                    aria-label="Edit height"
                  >
                    <IconEdit size={14} color="#8B3166" />
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* Badges: category + OOR status */}
        {(ctxLabel || oorLabel) && (
          <div className={styles.badgeRow}>
            {ctxLabel && <span className={styles.badge}>{ctxLabel}</span>}
            {oorLabel && <span className={styles.badgeOOR}>{oorLabel}</span>}
          </div>
        )}

        {/* Note */}
        {reading.note && <p className={styles.latestNote}>"{reading.note}"</p>}
      </div>

      {/* Divider + ref range — shown for BP and GLUCOSE only */}
      {reading.parameter !== VitalsParameter.WEIGHT &&
        reading.parameter !== VitalsParameter.HEIGHT && (
          <>
            <div className={styles.latestDivider} />
            <div className={styles.refRow}>
              <span className={styles.refLabel}>{refLabel}</span>
              {rangeNumbers && (
                <span className={styles.refValue}>{rangeNumbers}</span>
              )}
              <button
                className={styles.editRangeBtn}
                type="button"
                aria-label="Edit range"
                onClick={onEditRange}
              >
                <IconEdit size={16} color="#8B3166" />
              </button>
            </div>
          </>
        )}
    </div>
  );
};

// ── Delete dialog ─────────────────────────────────────────────────────────────

const DeleteDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  deleting: boolean;
}> = ({ isOpen, onClose, onConfirm, deleting }) => {
  const { t } = useTranslation();
  return (
    <MDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t("vitalsTracker.readingsDetail.deleteTitle")}
      primaryCtaText={
        deleting
          ? t("common.deleting")
          : t("vitalsTracker.readingsDetail.deleteYes")
      }
      onPrimaryCta={onConfirm}
      isPrimaryCtaDisabled={deleting}
      secondaryCtaText={t("vitalsTracker.readingsDetail.deleteNo")}
      onSecondaryCta={onClose}
    >
      <p className={styles.deleteBody}>
        {t("vitalsTracker.readingsDetail.deleteBody")}
      </p>
    </MDialog>
  );
};

// ── Drawer animation variants ─────────────────────────────────────────────────

const backdropVariants = {
  visible: { opacity: 1, pointerEvents: "auto" as const },
  hidden: { opacity: 0, pointerEvents: "none" as const },
};
const drawerVariants = { hidden: { y: "100%" }, visible: { y: 0 } };

// ── BMI Info Drawer ───────────────────────────────────────────────────────────

const BmiInfoDrawer: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  return (
    <DrawerPortal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={styles.backdrop}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>
      <div className={styles.drawerWrapper}>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className={styles.drawer}
              variants={drawerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ type: "tween", duration: 0.25 }}
            >
              <div className={styles.dragBar} />
              <div className={styles.drawerHeader}>
                <span className={styles.drawerTitle}>
                  {t("vitalsTracker.readingsDetail.bmiInfo.title")}
                </span>
                <button
                  type="button"
                  className={styles.drawerCloseBtn}
                  onClick={onClose}
                  aria-label="Close"
                >
                  <IconX size={18} />
                </button>
              </div>
              <div className={styles.drawerBody}>
                <p className={styles.bmiInfoText}>
                  {t("vitalsTracker.readingsDetail.bmiInfo.body")}
                </p>
                <p className={styles.bmiInfoText}>
                  {t("vitalsTracker.readingsDetail.bmiInfo.rangesTitle")}
                </p>
                <ul className={styles.bmiRangeList}>
                  <li>{t("vitalsTracker.readingsDetail.bmiInfo.low")}</li>
                  <li>{t("vitalsTracker.readingsDetail.bmiInfo.standard")}</li>
                  <li>
                    {t("vitalsTracker.readingsDetail.bmiInfo.borderline")}
                  </li>
                  <li>{t("vitalsTracker.readingsDetail.bmiInfo.high")}</li>
                </ul>
                <p className={styles.bmiInfoText}>
                  {t("vitalsTracker.readingsDetail.bmiInfo.disclaimer")}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DrawerPortal>
  );
};

// ── Date Range Drawer ─────────────────────────────────────────────────────────

const DateRangeDrawer: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onDownload: (startDate?: Date, endDate?: Date) => void;
  downloading: boolean;
}> = ({ isOpen, onClose, onDownload, downloading }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"all" | "range">("all");
  const [startDateObj, setStartDateObj] = useState<Date | null>(null);
  const [endDateObj, setEndDateObj] = useState<Date | null>(null);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  const formatDisplay = (d: Date | null) =>
    d
      ? d.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "Select date";

  const handleDownload = () => {
    if (mode === "all") {
      onDownload(undefined, undefined);
    } else {
      onDownload(startDateObj ?? undefined, endDateObj ?? undefined);
    }
  };

  return (
    <>
      <DrawerPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className={styles.backdrop}
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              onClick={onClose}
            />
          )}
        </AnimatePresence>
        <div className={styles.drawerWrapper}>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className={styles.drawer}
                variants={drawerVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={{ type: "tween", duration: 0.25 }}
              >
                <div className={styles.dragBar} />
                <div className={styles.dateRangeHeader}>
                  <h2 className={styles.dateRangeTitle}>
                    {t("vitalsTracker.readingsDetail.dateRangeDrawer.title")}
                  </h2>
                  <p className={styles.dateRangeSubtitle}>
                    {t("vitalsTracker.readingsDetail.dateRangeDrawer.subtitle")}
                  </p>
                </div>
                <div className={styles.dateRangeDivider} />
                <div className={styles.dateRangeBody}>
                  {/* Option 1 — Include all dates */}
                  <button
                    type="button"
                    className={styles.dateRangeOption}
                    onClick={() => setMode("all")}
                  >
                    <span
                      className={`${styles.radioCircle} ${
                        mode === "all" ? styles.radioCircleSelected : ""
                      }`}
                    >
                      {mode === "all" && (
                        <span className={styles.radioCircleInner} />
                      )}
                    </span>
                    <span className={styles.dateRangeOptionLabel}>
                      {t(
                        "vitalsTracker.readingsDetail.dateRangeDrawer.allDates"
                      )}
                    </span>
                  </button>
                  {/* Option 2 — Set date-range */}
                  <button
                    type="button"
                    className={styles.dateRangeOption}
                    onClick={() => setMode("range")}
                  >
                    <span
                      className={`${styles.radioCircle} ${
                        mode === "range" ? styles.radioCircleSelected : ""
                      }`}
                    >
                      {mode === "range" && (
                        <span className={styles.radioCircleInner} />
                      )}
                    </span>
                    <div className={styles.dateRangePickerGroup}>
                      <span className={styles.dateRangeSetLabel}>
                        {t(
                          "vitalsTracker.readingsDetail.dateRangeDrawer.setRange"
                        )}
                      </span>
                      <div className={styles.dateRangePickers}>
                        <button
                          type="button"
                          className={styles.datePickerTrigger}
                          onClick={(e) => {
                            e.stopPropagation();
                            setStartPickerOpen(true);
                            setMode("range");
                          }}
                        >
                          <span className={styles.datePickerText}>
                            {formatDisplay(startDateObj)}
                          </span>
                          <IconCalendarWeekFilled size={16} color="#8764bb" />
                        </button>
                        <span className={styles.dateRangeSep}>-</span>
                        <button
                          type="button"
                          className={styles.datePickerTrigger}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEndPickerOpen(true);
                            setMode("range");
                          }}
                        >
                          <span className={styles.datePickerText}>
                            {formatDisplay(endDateObj)}
                          </span>
                          <IconCalendarWeekFilled size={16} color="#8764bb" />
                        </button>
                      </div>
                    </div>
                  </button>
                </div>
                <div className={styles.dateRangeActions}>
                  <button
                    type="button"
                    className={styles.drCancelBtn}
                    onClick={onClose}
                  >
                    {t("vitalsTracker.readingsDetail.dateRangeDrawer.cancel")}
                  </button>
                  <button
                    type="button"
                    className={styles.drDownloadBtn}
                    onClick={handleDownload}
                    disabled={downloading}
                  >
                    {t("vitalsTracker.readingsDetail.dateRangeDrawer.download")}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DrawerPortal>
      <MDatePicker
        open={startPickerOpen}
        onClose={() => setStartPickerOpen(false)}
        onDone={(d) => {
          setStartDateObj(d);
          setStartPickerOpen(false);
        }}
        initialValue={startDateObj ?? undefined}
        title="Start Date"
        maxDate={endDateObj ?? new Date()}
      />
      <MDatePicker
        open={endPickerOpen}
        onClose={() => setEndPickerOpen(false)}
        onDone={(d) => {
          setEndDateObj(d);
          setEndPickerOpen(false);
        }}
        initialValue={endDateObj ?? undefined}
        title="End Date"
        minDate={startDateObj ?? undefined}
        maxDate={new Date()}
      />
    </>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const VitalsReadingsDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { parameter } = useParams<{ parameter: string }>();
  const param =
    (parameter?.toUpperCase() as VitalsParameter) ?? VitalsParameter.BP;

  const memberInfo = getMemberInfo();
  const memberId = memberInfo?.id ?? "";
  const isOwnProfile = !memberInfo?.onBehalf;
  const memberName = isOwnProfile
    ? "Your"
    : memberInfo?.nickname ?? memberInfo?.firstName ?? "Their";
  // "Add it to your readings?" / "Add it to Priya's readings?"
  const memberPossessive = isOwnProfile
    ? "your"
    : `${memberInfo?.nickname ?? memberInfo?.firstName ?? "their"}'s`;

  const {
    config,
    isLoading: configLoading,
    refreshConfig,
  } = useVitalsTracking(memberId);
  const {
    readings,
    total,
    valueRanges,
    isLoading: readingsLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    refresh,
    deleteReading,
  } = useVitalsReadings(memberId, param);

  const [activeTab, setActiveTab] = useState<"chart" | "table">("chart");

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editReading, setEditReading] = useState<VitalsReading | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VitalsReading | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editRangeOpen, setEditRangeOpen] = useState(false);
  const [weightUnit, setWeightUnit] = useState<"kg" | "lb">("kg");
  const [addHeightOpen, setAddHeightOpen] = useState(false);
  const [glucoseFilter, setGlucoseFilter] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [bmiInfoOpen, setBmiInfoOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  // Suggestions only ever exist for BP and weight — skip the call elsewhere
  const { suggestions, isActioning, actOn } = usePrescriptionVitals(memberId, {
    enabled: param === VitalsParameter.BP || param === VitalsParameter.WEIGHT,
  });
  const prescriptionSuggestion =
    suggestions.find((s) => s.parameter === param) ?? null;

  const handleSuggestionAdd = async () => {
    if (!prescriptionSuggestion) return;
    await actOn(prescriptionSuggestion, "add");
    await Promise.all([refresh(), refreshConfig()]);
  };

  const handleSuggestionIgnore = async () => {
    if (!prescriptionSuggestion) return;
    await actOn(prescriptionSuggestion, "ignore");
  };

  const range = config?.ranges?.[param] ?? {};
  const latestReading = config?.latestReadings?.[param];

  // A single reading renders as range strips instead of a trend, so the
  // Chart/Table toggle has nothing to switch between.
  const showTabs = readings.length > 1;
  const visibleTab = showTabs ? activeTab : "chart";

  const filteredReadings = useMemo(
    () =>
      glucoseFilter
        ? readings.filter((r) => r.values.category === glucoseFilter)
        : readings,
    [readings, glucoseFilter]
  );

  const title =
    param === VitalsParameter.BP
      ? t("vitalsTracker.bloodPressure")
      : param === VitalsParameter.GLUCOSE
      ? t("vitalsTracker.bloodSugar")
      : param === VitalsParameter.HEIGHT
      ? t("vitalsTracker.heightVital")
      : t("vitalsTracker.weight");

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteReading(deleteTarget.id);
      await refreshConfig();
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleteReading, refreshConfig]);

  const handleShare = useCallback(() => {
    setDateRangeOpen(true);
  }, []);

  const handleDownload = useCallback(
    async (startDate?: Date, endDate?: Date) => {
      if (!memberId || sharing) return;
      setDateRangeOpen(false);

      // On native device: hand off to the app with date range params
      const handed = triggerNativeVitalsShare({
        memberId,
        memberName,
        parameter: param,
        parameterName: title,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (handed) return;

      // Web fallback: download PDF directly
      setSharing(true);
      try {
        const blob = await vitalsTrackingService.downloadVitalsPdf(
          memberId,
          param,
          startDate,
          endDate
        );
        const filename = `${memberName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")}-${param.toLowerCase()}.pdf`;
        const file = new File([blob], filename, { type: "application/pdf" });
        const navAny = navigator as any;
        if (
          navAny.canShare &&
          navAny.canShare({ files: [file] }) &&
          navigator.share
        ) {
          await navigator.share({ title, files: [file] });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch {
        // Share cancelled or unsupported — silently ignore
      } finally {
        setSharing(false);
      }
    },
    [memberId, memberName, param, title, sharing]
  );

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <MButton variant="back" onClick={() => navigate(-1)} />
        <h1 className={styles.headerTitle}>{title}</h1>
        <button
          className={styles.exitBtn}
          onClick={() => navigate(-1)}
          type="button"
        >
          {t("vitalsTracker.readingsDetail.exit")}
        </button>
      </div>

      {/* Action bar */}
      <div className={styles.actionBar}>
        <button
          type="button"
          className={styles.reminderBtn}
          onClick={() =>
            // Ask the reminders list to pop back to this screen's existing
            // history entry rather than pushing a fresh copy of it. Pushing
            // grew the stack on every Back tap, so Back bounced between
            // reminders and this screen forever instead of leaving.
            navigate("/reminders", {
              state: { returnTo: NAVIGATE_BACK_IN_HISTORY },
            })
          }
        >
          <IconCalendarTime size={20} color="#36116d" />
          <span>{t("vitalsTracker.readingsDetail.setReminder")}</span>
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={handleShare}
          disabled={sharing}
          aria-label="Share"
        >
          <IconShare3 size={24} color="#36116d" />
        </button>
      </div>

      {configLoading || readingsLoading ? (
        <div className={styles.loaderWrap}>
          <MLoader />
        </div>
      ) : (
        <div className={styles.content}>
          {prescriptionSuggestion && (
            <PrescriptionVitalCard
              suggestion={prescriptionSuggestion}
              memberPossessive={memberPossessive}
              onAdd={handleSuggestionAdd}
              onIgnore={handleSuggestionIgnore}
              loading={isActioning}
            />
          )}

          {latestReading && (
            <LatestReadingCard
              reading={latestReading}
              range={range}
              memberName={memberName}
              onEditRange={() => setEditRangeOpen(true)}
              weightUnit={weightUnit}
              onWeightUnitChange={setWeightUnit}
              onAddHeight={() => setAddHeightOpen(true)}
            />
          )}

          <div className={styles.contentDivider} />

          {/* Add reading */}
          <button
            className={styles.addBtn}
            onClick={() =>
              param === VitalsParameter.HEIGHT
                ? setAddHeightOpen(true)
                : setAddModalOpen(true)
            }
            type="button"
          >
            <IconPlus size={16} />
            <span>{t("vitalsTracker.readingsDetail.addReading")}</span>
          </button>

          {/* Tab toggle — a lone reading has no trend to compare views on */}
          {showTabs && (
            <div className={styles.tabContainer}>
              <div className={styles.tabTrack}>
                <button
                  className={`${styles.tabItem}${
                    activeTab === "chart" ? ` ${styles.tabActive}` : ""
                  }`}
                  onClick={() => {
                    setActiveTab("chart");
                    setGlucoseFilter(null);
                  }}
                  type="button"
                >
                  {t("vitalsTracker.readingsDetail.chart")}
                </button>
                <button
                  className={`${styles.tabItem}${
                    activeTab === "table" ? ` ${styles.tabActive}` : ""
                  }`}
                  onClick={() => setActiveTab("table")}
                  type="button"
                >
                  {t("vitalsTracker.readingsDetail.table")}
                </button>
              </div>
            </div>
          )}

          {/* Glucose category filter chips — table view only */}
          {param === VitalsParameter.GLUCOSE && visibleTab === "table" && (
            <div className={styles.filterRow}>
              {(
                [
                  null,
                  "Fasting",
                  "Post-prandial",
                  "Random",
                  "Before bed",
                ] as const
              ).map((cat) => (
                <button
                  key={cat ?? "all"}
                  type="button"
                  className={`${styles.filterChip}${
                    glucoseFilter === cat ? ` ${styles.filterChipActive}` : ""
                  }`}
                  onClick={() => setGlucoseFilter(cat)}
                >
                  {cat === null ? t("common.all") : cat}
                </button>
              ))}
            </div>
          )}

          {/* Content */}
          {visibleTab === "chart" ? (
            <ChartView
              readings={filteredReadings}
              range={range}
              param={param}
              total={filteredReadings.length}
              valueRanges={valueRanges}
              glucoseFilter={glucoseFilter}
              onBmiInfoClick={() => setBmiInfoOpen(true)}
              hasMore={hasMore}
              onLoadMore={loadMore}
            />
          ) : null}

          {/* With the toggle hidden, the single reading still needs its row so
              it stays editable and deletable. */}
          {(visibleTab === "table" || !showTabs) && (
            <TableView
              readings={filteredReadings}
              range={range}
              param={param}
              onEdit={setEditReading}
              onDelete={setDeleteTarget}
              weightUnit={weightUnit}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMore}
              glucoseFilter={glucoseFilter}
            />
          )}
        </div>
      )}

      {addModalOpen && (
        <AddReadingModal
          key="new"
          isOpen
          onClose={() => setAddModalOpen(false)}
          parameter={param}
          memberId={memberId}
          onSuccess={async () => {
            await Promise.all([refresh(), refreshConfig()]);
            setAddModalOpen(false);
          }}
          isFirstTime={false}
        />
      )}
      {editReading && (
        <AddReadingModal
          key={`edit-${editReading.id}`}
          isOpen
          onClose={() => setEditReading(null)}
          parameter={param}
          memberId={memberId}
          onSuccess={async () => {
            await Promise.all([refresh(), refreshConfig()]);
            setEditReading(null);
          }}
          isFirstTime={false}
          editReading={editReading}
        />
      )}
      <DeleteDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        deleting={deleting}
      />
      {editRangeOpen &&
        param !== VitalsParameter.WEIGHT &&
        param !== VitalsParameter.HEIGHT && (
          <EditRangeModal
            isOpen
            onClose={() => setEditRangeOpen(false)}
            parameter={param as VitalsParameter.BP | VitalsParameter.GLUCOSE}
            memberId={memberId}
            currentRange={range}
            onSuccess={async () => {
              await Promise.all([refresh(), refreshConfig()]);
            }}
          />
        )}
      <AddHeightModal
        isOpen={addHeightOpen}
        onClose={() => setAddHeightOpen(false)}
        memberId={memberId}
        currentHeight={
          param === VitalsParameter.HEIGHT ? undefined : range.height
        }
        mode={param === VitalsParameter.HEIGHT ? "reading" : "config"}
        onSuccess={async () => {
          await Promise.all([refresh(), refreshConfig()]);
          setAddHeightOpen(false);
        }}
      />
      <BmiInfoDrawer
        isOpen={bmiInfoOpen}
        onClose={() => setBmiInfoOpen(false)}
      />
      <DateRangeDrawer
        isOpen={dateRangeOpen}
        onClose={() => setDateRangeOpen(false)}
        onDownload={handleDownload}
        downloading={sharing}
      />
    </div>
  );
};

export default VitalsReadingsDetail;
